import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { API_URL } from '../config';

/**
 * TwilioSoftphone — perzistentní volací panel dole (~1/10 obrazovky).
 * Zobrazí se jen povoleným uživatelům. Umí:
 *  - odchozí hovory (výběr caller ID z přidělených čísel)
 *  - příchozí hovory (přijmout/odmítnout)
 *  - SMS
 *  - výpis posledních hovorů
 * Reaguje na globální eventy z karty zákazníka:
 *  - window 'twilio:call'  detail: { number, companyId, companyName, contactName }
 *  - window 'twilio:sms'   detail: { number, companyId, companyName, contactName }
 */
export default function TwilioSoftphone({ userId, isDarkMode }) {
  const navigate = useNavigate();
  const [userCfg, setUserCfg] = useState(null); // { enabled, numbers, recordingEnabled, ready }
  const [deviceReady, setDeviceReady] = useState(false);
  const [deviceError, setDeviceError] = useState('');
  const [expanded, setExpanded] = useState(false);
  const [dialNumber, setDialNumber] = useState('');
  const [callerId, setCallerId] = useState('');
  const [activeCall, setActiveCall] = useState(null); // Twilio Call object
  const [callState, setCallState] = useState('idle'); // idle | connecting | active | incoming
  const [incoming, setIncoming] = useState(null); // Twilio Call (incoming)
  const [callInfo, setCallInfo] = useState(null); // { number, companyName, contactName, startedAt }
  const [muted, setMuted] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [recentCalls, setRecentCalls] = useState([]);
  const [showLog, setShowLog] = useState(false);
  const [logFilter, setLogFilter] = useState('all'); // all | missed
  const [missedSeenAt, setMissedSeenAt] = useState(0);
  const [smsModal, setSmsModal] = useState(null); // { number, companyId, companyName, body, sending }

  const provider = userCfg?.provider || 'twilio';

  const deviceRef = useRef(null);
  const timerRef = useRef(null);
  const notifiedCallsRef = useRef(new Set());

  // Zmeškaný hovor = příchozí, který nebyl přijat (nezvednutý/odmítnutý/nedostupný)
  const isMissedCall = (c) => c.direction === 'inbound' && ['no-answer', 'busy', 'failed', 'canceled'].includes(c.status);
  const missedCalls = recentCalls.filter(isMissedCall);
  const unseenMissedCount = missedCalls.filter(c => new Date(c.startedAt).getTime() > missedSeenAt).length;

  const markMissedSeen = useCallback(() => {
    const now = Date.now();
    setMissedSeenAt(now);
    if (userId) localStorage.setItem(`twilio_missed_seen_${userId}`, String(now));
  }, [userId]);

  // ---- Načti čas posledního zobrazení zmeškaných hovorů (per uživatel) ----
  useEffect(() => {
    if (!userId) return;
    const stored = parseInt(localStorage.getItem(`twilio_missed_seen_${userId}`) || '0', 10);
    setMissedSeenAt(stored || 0);
  }, [userId]);

  // ---- Načti user-config ----
  const loadUserCfg = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/twilio/user-config?userId=${userId}`);
      if (res.ok) {
        const d = await res.json();
        setUserCfg(d);
        if (d.numbers?.length && !callerId) setCallerId(d.numbers[0]);
      }
    } catch { /* ignore */ }
  }, [userId, callerId]);

  useEffect(() => { if (userId) loadUserCfg(); }, [userId, loadUserCfg]);

  // Odsazení stránky, aby spodní lišta telefonu nepřekrývala obsah/tlačítka
  useEffect(() => {
    if (userCfg?.enabled) {
      const prev = document.body.style.paddingBottom;
      document.body.style.paddingBottom = '5rem';
      return () => { document.body.style.paddingBottom = prev; };
    }
  }, [userCfg?.enabled]);

  // ---- Inicializace Twilio Device ----
  useEffect(() => {
    if (!userCfg?.enabled || !userCfg?.ready) return;
    // Odorik nepoužívá softphone v prohlížeči (volá se přes zpětné zavolání z karty firmy)
    if (userCfg?.provider === 'odorik') return;
    let cancelled = false;
    let device = null;

    (async () => {
      // Fáze 1: načtení Voice SDK
      let Device;
      try {
        const mod = await import('@twilio/voice-sdk');
        Device = mod.Device || mod.default?.Device;
        if (!Device) throw new Error('Modul @twilio/voice-sdk neobsahuje Device');
      } catch (e) {
        console.error('[twilio softphone] načtení SDK selhalo:', e);
        setDeviceError('Balíček @twilio/voice-sdk není v buildu frontendu (přebuild + hard refresh).');
        return;
      }

      try {
        // Fáze 2: token
        const tokenRes = await fetch(`${API_URL}/twilio/token?userId=${userId}`);
        if (!tokenRes.ok) {
          const err = await tokenRes.json().catch(() => ({}));
          setDeviceError(err.error || `Token se nepodařilo získat (HTTP ${tokenRes.status})`);
          return;
        }
        const { token } = await tokenRes.json();
        if (cancelled) return;

        // Fáze 3: inicializace zařízení
        // edge: evropské edge lokace (Frankfurt/Dublin) sníží latenci pro CZ – média nejdou přes USA,
        // což řeší „sekání" prvních sekund hovoru. Fallback na ashburn (US) když EU nedostupné.
        device = new Device(token, {
          codecPreferences: ['opus', 'pcmu'],
          edge: ['frankfurt', 'dublin', 'ashburn'],
          logLevel: 'error'
        });
        deviceRef.current = device;

        device.on('registered', () => { if (!cancelled) { setDeviceReady(true); setDeviceError(''); } });
        device.on('error', (err) => {
          console.error('[twilio softphone] device error:', err, 'code:', err?.code, 'twilioError:', err?.twilioError);
          const code = err?.code ? ` (kód ${err.code})` : '';
          setDeviceError((err?.message || err?.twilioError?.message || 'Chyba zařízení') + code);
        });
        device.on('incoming', (call) => {
          const fromNum = call.parameters?.From || 'Neznámé číslo';
          setIncoming(call);
          setCallState('incoming');
          setCallInfo({ number: fromNum, startedAt: null });
          // Dohledej firmu podle čísla volajícího → nabídne tlačítko „Otevřít firmu"
          (async () => {
            try {
              const res = await fetch(`${API_URL}/twilio/company-by-phone?userId=${userId}&phone=${encodeURIComponent(fromNum)}`);
              if (res.ok) { const d = await res.json(); if (d.found) setCallInfo(ci => ({ ...(ci || {}), companyId: d.companyId, companyName: d.companyName })); }
            } catch { /* ignore */ }
          })();
          call.on('cancel', () => { setIncoming(null); setCallState('idle'); });
          call.on('disconnect', () => cleanupCall());
        });
        device.on('tokenWillExpire', async () => {
          try {
            const r = await fetch(`${API_URL}/twilio/token?userId=${userId}`);
            if (r.ok) { const { token: t } = await r.json(); device.updateToken(t); }
          } catch { /* ignore */ }
        });

        // Zařízení je připravené pro ODCHOZÍ hovory hned po vytvoření (token je platný).
        if (!cancelled) setDeviceReady(true);

        // Registrace je potřeba jen pro PŘÍCHOZÍ hovory – ať nespadne celý telefon, když selže.
        device.register().catch((err) => {
          console.warn('[twilio softphone] registrace (příchozí) selhala:', err, 'code:', err?.code);
          const code = err?.code ? ` (kód ${err.code})` : '';
          setDeviceError(`Příchozí hovory nedostupné: ${err?.message || err?.name || 'chyba registrace'}${code}. Odchozí volání funguje.`);
        });
      } catch (e) {
        console.error('[twilio softphone] inicializace selhala:', e, 'code:', e?.code, 'name:', e?.name);
        const detail = e?.message || e?.twilioError?.message || e?.name || (typeof e === 'string' ? e : JSON.stringify(e));
        const code = e?.code ? ` (kód ${e.code})` : '';
        setDeviceError(`Inicializace: ${detail || 'neznámá chyba'}${code}`);
      }
    })();

    return () => {
      cancelled = true;
      try { device && device.destroy(); } catch { /* ignore */ }
      deviceRef.current = null;
      setDeviceReady(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userCfg?.enabled, userCfg?.ready, userId]);

  // ---- Timer aktivního hovoru ----
  useEffect(() => {
    if (callState === 'active') {
      timerRef.current = setInterval(() => setElapsed(e => e + 1), 1000);
    } else {
      clearInterval(timerRef.current);
      if (callState === 'idle') setElapsed(0);
    }
    return () => clearInterval(timerRef.current);
  }, [callState]);

  const cleanupCall = () => {
    setActiveCall(null);
    setIncoming(null);
    setCallState('idle');
    setMuted(false);
    setCallInfo(null);
    fetchRecentCalls();
  };

  const fetchRecentCalls = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/twilio/calls?userId=${userId}&agentUserId=${userId}&limit=20`);
      if (res.ok) { const d = await res.json(); setRecentCalls(d.calls || []); }
    } catch { /* ignore */ }
  }, [userId]);

  useEffect(() => { if (userCfg?.enabled) fetchRecentCalls(); }, [userCfg?.enabled, fetchRecentCalls]);

  // Pravidelně kontroluj nové hovory (zachytí zmeškané hovory i když je panel zavřený)
  useEffect(() => {
    if (!userCfg?.enabled) return;
    const id = setInterval(fetchRecentCalls, 20000);
    return () => clearInterval(id);
  }, [userCfg?.enabled, fetchRecentCalls]);

  // Vyžádej si oprávnění na desktop notifikace (jen jednou, po povolení VoIP panelu)
  useEffect(() => {
    if (userCfg?.enabled && typeof Notification !== 'undefined' && Notification.permission === 'default') {
      Notification.requestPermission().catch(() => { /* ignore */ });
    }
  }, [userCfg?.enabled]);

  // Desktop notifikace na nový zmeškaný hovor (jen na hovory z posledních 2 minut, aby nespamovaly staré)
  useEffect(() => {
    if (!userId) return;
    missedCalls.forEach(c => {
      const startedAt = new Date(c.startedAt).getTime();
      if (startedAt <= missedSeenAt || notifiedCallsRef.current.has(c._id)) return;
      notifiedCallsRef.current.add(c._id);
      if (Date.now() - startedAt > 2 * 60 * 1000) return;
      if (typeof Notification !== 'undefined' && Notification.permission === 'granted') {
        new Notification('☎️ Zmeškaný hovor', { body: c.fromNumber + (c.companyName ? ` · ${c.companyName}` : '') });
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recentCalls, userId, missedSeenAt]);

  // ---- Odchozí hovor ----
  const startCall = useCallback(async (number, meta = {}) => {
    if (!number) return;
    const device = deviceRef.current;
    if (!device) return;
    const cleaned = String(number).replace(/[^\d+]/g, '');
    const toE164 = cleaned.startsWith('+') ? cleaned : (cleaned.startsWith('00') ? '+' + cleaned.slice(2) : (cleaned.length === 9 ? '+420' + cleaned : '+' + cleaned));
    try {
      setCallState('connecting');
      setCallInfo({ number: toE164, companyName: meta.companyName, contactName: meta.contactName, startedAt: Date.now() });
      const call = await device.connect({
        params: { To: toE164, callerId: callerId || (userCfg?.numbers?.[0] || ''), companyId: meta.companyId || '' }
      });
      setActiveCall(call);
      call.on('accept', () => setCallState('active'));
      call.on('disconnect', () => cleanupCall());
      call.on('cancel', () => cleanupCall());
      call.on('reject', () => cleanupCall());
    } catch (e) {
      setDeviceError(e?.message || 'Hovor se nepodařilo navázat');
      setCallState('idle');
    }
  }, [callerId, userCfg]);

  const acceptIncoming = () => {
    if (!incoming) return;
    incoming.accept();
    setActiveCall(incoming);
    setCallState('active');
    setCallInfo(ci => ({ ...(ci || {}), startedAt: Date.now() }));
    incoming.on('disconnect', () => cleanupCall());
    setIncoming(null);
  };
  const rejectIncoming = () => { if (incoming) { incoming.reject(); } setIncoming(null); setCallState('idle'); };
  const hangup = () => {
    try { activeCall && activeCall.disconnect(); } catch { /* ignore */ }
    try { deviceRef.current && deviceRef.current.disconnectAll(); } catch { /* ignore */ }
    cleanupCall();
  };
  const toggleMute = () => { if (activeCall) { const m = !muted; activeCall.mute(m); setMuted(m); } };
  const sendDigit = (d) => { if (activeCall) activeCall.sendDigits(d); else setDialNumber(n => n + d); };
  const openCompany = () => { if (callInfo?.companyId) { navigate(`/companies/${callInfo.companyId}`); setExpanded(false); } };

  // ---- SMS ----
  const sendSms = async () => {
    if (!smsModal?.number || !smsModal?.body) return;
    setSmsModal(m => ({ ...m, sending: true }));
    try {
      const res = await fetch(`${API_URL}/twilio/sms`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId, to: smsModal.number, body: smsModal.body, from: callerId, companyId: smsModal.companyId })
      });
      if (res.ok) { setSmsModal(null); alert('SMS odeslána.'); }
      else { const e = await res.json().catch(() => ({})); alert(e.error || 'Chyba při odesílání SMS'); setSmsModal(m => ({ ...m, sending: false })); }
    } catch { alert('Chyba připojení'); setSmsModal(m => ({ ...m, sending: false })); }
  };

  // ---- Globální eventy z karty zákazníka ----
  useEffect(() => {
    // Odorik obsluhuje komponenta OdorikSipPhone – tady nic neděláme
    const isOdorik = userCfg?.provider === 'odorik';
    const onCall = (e) => {
      if (isOdorik) return;
      const { number, companyId, companyName, contactName } = e.detail || {};
      if (!number) return;
      startCall(number, { companyId, companyName, contactName });
    };
    const onSms = (e) => {
      if (isOdorik) return;
      const { number, companyId, companyName } = e.detail || {};
      if (!number) return;
      const cleaned = String(number).replace(/[^\d+]/g, '');
      const toE164 = cleaned.startsWith('+') ? cleaned : (cleaned.length === 9 ? '+420' + cleaned : '+' + cleaned);
      setSmsModal({ number: toE164, companyId, companyName, body: '', sending: false });
      setExpanded(true);
    };
    window.addEventListener('twilio:call', onCall);
    window.addEventListener('twilio:sms', onSms);
    return () => { window.removeEventListener('twilio:call', onCall); window.removeEventListener('twilio:sms', onSms); };
  }, [startCall, userCfg?.provider]);

  if (!userCfg?.enabled) return null;

  const fmtTime = (s) => `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;
  const bg = isDarkMode ? 'bg-gray-900 border-gray-700 text-gray-100' : 'bg-white border-gray-200 text-gray-900';
  const btn = 'rounded-lg font-semibold transition disabled:opacity-40';

  // Odorik řeší samostatná komponenta OdorikSipPhone (přímé WebRTC volání)
  if (provider === 'odorik') return null;

  const panel = (
    <>
      {/* Rozšířený panel (dialpad / hovor / log) */}
      {expanded && (
        <div className={`fixed bottom-14 right-3 z-[9998] w-80 max-w-[92vw] rounded-xl border shadow-2xl ${bg}`}>
          <div className={`flex items-center justify-between px-3 py-2 border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
            <span className="text-sm font-semibold">☎️ Telefon</span>
            <div className="flex items-center gap-2">
              <button onClick={() => { setShowLog(true); setLogFilter('missed'); markMissedSeen(); }} title="Zmeškané hovory" className="relative text-xs px-2 py-1 rounded bg-gray-500/20 hover:bg-gray-500/40">
                📵
                {unseenMissedCount > 0 && (
                  <span className="absolute -top-1.5 -right-1.5 bg-red-600 text-white text-[10px] rounded-full px-1 min-w-[15px] leading-[15px] text-center">{unseenMissedCount}</span>
                )}
              </button>
              <button onClick={() => setShowLog(s => !s)} title="Historie" className="text-xs px-2 py-1 rounded bg-gray-500/20 hover:bg-gray-500/40">🕘</button>
              <button onClick={() => setExpanded(false)} className="text-lg leading-none">▾</button>
            </div>
          </div>

          <div className="p-3 space-y-3">
            {/* Stav hovoru */}
            {callState !== 'idle' && (
              <div className={`rounded-lg p-3 text-center ${isDarkMode ? 'bg-gray-800' : 'bg-gray-100'}`}>
                <div className="text-xs opacity-70">
                  {callState === 'incoming' ? '📥 Příchozí hovor' : callState === 'connecting' ? 'Vytáčím…' : '🟢 Hovor probíhá'}
                </div>
                <div className="text-base font-bold font-mono mt-1">{callInfo?.number || ''}</div>
                {callInfo?.companyName && <div className="text-xs">{callInfo.companyName}{callInfo.contactName ? ` · ${callInfo.contactName}` : ''}</div>}
                {callInfo?.companyId && (
                  <button onClick={openCompany} className="mt-2 text-xs px-3 py-1 rounded bg-blue-600 hover:bg-blue-700 text-white">🏢 Otevřít firmu</button>
                )}
                {callState === 'active' && <div className="text-sm mt-1">{fmtTime(elapsed)}</div>}

                <div className="flex justify-center gap-2 mt-3">
                  {callState === 'incoming' ? (
                    <>
                      <button onClick={acceptIncoming} className={`${btn} bg-green-600 hover:bg-green-700 text-white px-4 py-2`}>✅ Přijmout</button>
                      <button onClick={rejectIncoming} className={`${btn} bg-red-600 hover:bg-red-700 text-white px-4 py-2`}>⛔ Odmítnout</button>
                    </>
                  ) : (
                    <>
                      {callState === 'active' && (
                        <button onClick={toggleMute} className={`${btn} px-3 py-2 ${muted ? 'bg-yellow-500 text-white' : (isDarkMode ? 'bg-gray-700' : 'bg-gray-200')}`}>{muted ? '🔇' : '🎙️'}</button>
                      )}
                      <button onClick={hangup} className={`${btn} bg-red-600 hover:bg-red-700 text-white px-4 py-2`}>📵 Zavěsit</button>
                    </>
                  )}
                </div>
              </div>
            )}

            {/* Historie hovorů */}
            {showLog ? (
              <div className="space-y-2">
                <div className="flex gap-1">
                  <button onClick={() => setLogFilter('all')} className={`flex-1 text-xs px-2 py-1 rounded ${logFilter === 'all' ? 'bg-indigo-600 text-white' : (isDarkMode ? 'bg-gray-800 hover:bg-gray-700' : 'bg-gray-100 hover:bg-gray-200')}`}>Vše</button>
                  <button onClick={() => { setLogFilter('missed'); markMissedSeen(); }} className={`flex-1 text-xs px-2 py-1 rounded ${logFilter === 'missed' ? 'bg-red-600 text-white' : (isDarkMode ? 'bg-gray-800 hover:bg-gray-700' : 'bg-gray-100 hover:bg-gray-200')}`}>📵 Zmeškané{missedCalls.length ? ` (${missedCalls.length})` : ''}</button>
                </div>
                <div className="max-h-52 overflow-y-auto space-y-1">
                  {(logFilter === 'missed' ? missedCalls : recentCalls).length === 0 ? (
                    <div className="text-xs opacity-60 text-center py-4">{logFilter === 'missed' ? 'Žádné zmeškané hovory' : 'Žádné hovory'}</div>
                  ) : (logFilter === 'missed' ? missedCalls : recentCalls).map(c => (
                    <button key={c._id} onClick={() => startCall(c.direction === 'inbound' ? c.fromNumber : c.toNumber, { companyId: c.companyId, companyName: c.companyName })}
                      className={`w-full text-left px-2 py-1.5 rounded text-xs flex items-center justify-between ${isMissedCall(c) ? (isDarkMode ? 'bg-red-900/30 hover:bg-red-900/50' : 'bg-red-50 hover:bg-red-100') : (isDarkMode ? 'hover:bg-gray-800' : 'hover:bg-gray-100')}`}>
                      <span>
                        <span className="mr-1">{isMissedCall(c) ? '📵' : (c.direction === 'inbound' ? '📥' : '📤')}</span>
                        <span className={`font-mono ${isMissedCall(c) ? 'text-red-500 font-semibold' : ''}`}>{c.direction === 'inbound' ? c.fromNumber : c.toNumber}</span>
                        {c.companyName && <span className="block opacity-60">{c.companyName}</span>}
                      </span>
                      <span className="opacity-60">{c.startedAt ? new Date(c.startedAt).toLocaleTimeString('cs-CZ', { hour: '2-digit', minute: '2-digit' }) : ''}</span>
                    </button>
                  ))}
                </div>
              </div>
            ) : callState === 'idle' && (
              <>
                {/* Vytáčení */}
                <input value={dialNumber} onChange={e => setDialNumber(e.target.value)} placeholder="Zadej číslo…"
                  className={`w-full px-3 py-2 rounded border text-center font-mono ${isDarkMode ? 'bg-gray-800 border-gray-700 text-white' : 'bg-white border-gray-300 text-gray-900'}`} />
                <div className="grid grid-cols-3 gap-1.5">
                  {['1', '2', '3', '4', '5', '6', '7', '8', '9', '*', '0', '#'].map(d => (
                    <button key={d} onClick={() => sendDigit(d)} className={`${btn} py-2 text-base ${isDarkMode ? 'bg-gray-800 hover:bg-gray-700' : 'bg-gray-100 hover:bg-gray-200'}`}>{d}</button>
                  ))}
                </div>
                <button onClick={() => startCall(dialNumber)} disabled={!dialNumber || !deviceReady} className={`${btn} w-full bg-green-600 hover:bg-green-700 text-white py-2.5`}>📞 Volat</button>
              </>
            )}

            {/* Výběr caller ID */}
            {(userCfg.numbers?.length > 1) && callState === 'idle' && !showLog && (
              <div>
                <label className="text-xs opacity-70">Volat z čísla:</label>
                <select value={callerId} onChange={e => setCallerId(e.target.value)} className={`w-full px-2 py-1.5 rounded border text-sm mt-1 ${isDarkMode ? 'bg-gray-800 border-gray-700 text-white' : 'bg-white border-gray-300'}`}>
                  {userCfg.numbers.map(n => <option key={n} value={n}>{n}</option>)}
                </select>
              </div>
            )}
            {callState === 'idle' && !showLog && (
              <div className="text-xs opacity-70 text-center">
                {callerId
                  ? <>Voláš z čísla: <span className="font-mono">{callerId}</span></>
                  : <span className="text-red-500">⚠️ Nemáš přiřazené žádné číslo – přidej ho v Nastavení → Twilio a ulož.</span>}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Spodní lišta (~1/10 obrazovky) */}
      <div className={`fixed bottom-0 left-0 right-0 z-[9997] border-t-2 ${isDarkMode ? 'bg-slate-900 border-indigo-500 text-slate-100' : 'bg-slate-700 border-indigo-400 text-slate-50'}`} style={{ height: '3.25rem' }}>
        <div className="max-w-7xl mx-auto h-full px-3 flex items-center gap-3">
          <button onClick={() => setExpanded(x => !x)} className="relative flex items-center gap-2 text-sm font-semibold">
            <span className={`w-2.5 h-2.5 rounded-full ${deviceReady ? 'bg-green-500' : (deviceError ? 'bg-red-500' : 'bg-yellow-400')}`} />
            ☎️ Telefon
            {unseenMissedCount > 0 && (
              <span className="ml-1 bg-red-600 text-white text-[10px] rounded-full px-1.5 min-w-[16px] leading-[16px] text-center animate-pulse">{unseenMissedCount}</span>
            )}
          </button>
          {/* Aktivní číslo (caller ID) + přepínač, když je přidělených víc (jen v klidu) */}
          {callState === 'idle' && (userCfg.numbers?.length > 0) && (
            userCfg.numbers.length > 1 ? (
              <div className="flex items-center gap-1">
                <span className="text-xs opacity-70 hidden sm:inline">aktivní:</span>
                <select
                  value={callerId}
                  onChange={e => setCallerId(e.target.value)}
                  title="Číslo, ze kterého voláš"
                  className={`px-2 py-1 rounded border text-xs font-mono ${isDarkMode ? 'bg-gray-800 border-gray-700 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                >
                  {userCfg.numbers.map(n => <option key={n} value={n}>{n}</option>)}
                </select>
              </div>
            ) : (
              <span className="text-xs font-mono opacity-90 hidden sm:inline" title="Číslo, ze kterého voláš">aktivní: {callerId}</span>
            )
          )}
          {callState === 'active' && <span className="text-sm font-mono">🟢 {callInfo?.number} · {fmtTime(elapsed)}</span>}
          {callState === 'incoming' && <span className="text-sm animate-pulse">📥 Příchozí: {callInfo?.number}</span>}
          {callState === 'connecting' && <span className="text-sm">Vytáčím {callInfo?.number}…</span>}
          {callState !== 'idle' && callInfo?.companyId && (
            <button onClick={openCompany} title="Otevřít kartu firmy" className="text-xs px-2 py-1 rounded bg-blue-600 hover:bg-blue-700 text-white max-w-[180px] truncate">🏢 {callInfo.companyName || 'Otevřít firmu'}</button>
          )}
          <div className="flex-1" />
          {callState === 'idle' ? (
            <>
              <input value={dialNumber} onChange={e => setDialNumber(e.target.value)} onKeyDown={e => e.key === 'Enter' && startCall(dialNumber)}
                placeholder="Rychlé vytáčení…" className={`w-40 px-2 py-1 rounded border text-sm font-mono ${isDarkMode ? 'bg-gray-800 border-gray-700 text-white' : 'bg-white border-gray-300 text-gray-900'}`} />
              <button onClick={() => startCall(dialNumber)} disabled={!dialNumber || !deviceReady} className={`${btn} bg-green-600 hover:bg-green-700 text-white px-3 py-1 text-sm`}>📞</button>
              <button onClick={() => { setExpanded(true); setShowLog(true); setLogFilter('all'); }} title="Protokol hovorů"
                className={`relative inline-flex items-center gap-1 ${btn} px-3 py-1 text-xs ${isDarkMode ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-200 hover:bg-gray-300'}`}>
                <span>📜</span>
                <span>Protokol</span>
                {unseenMissedCount > 0 && (
                  <span className="absolute -top-1.5 -right-1.5 bg-red-600 text-white text-[10px] rounded-full px-1 min-w-[15px] leading-[15px] text-center">{unseenMissedCount}</span>
                )}
              </button>
            </>
          ) : callState === 'incoming' ? (
            <div className="flex gap-2">
              <button onClick={acceptIncoming} className={`${btn} bg-green-600 hover:bg-green-700 text-white px-3 py-1 text-sm`}>✅ Přijmout</button>
              <button onClick={rejectIncoming} className={`${btn} bg-red-600 hover:bg-red-700 text-white px-3 py-1 text-sm`}>⛔</button>
            </div>
          ) : (
            <div className="flex gap-2">
              {callState === 'active' && (
                <button onClick={toggleMute} title={muted ? 'Zrušit ztlumení' : 'Ztlumit mikrofon'} className={`${btn} px-3 py-1 text-sm ${muted ? 'bg-yellow-500 text-white' : (isDarkMode ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-200 hover:bg-gray-300')}`}>{muted ? '🔇' : '🎙️'}</button>
              )}
              <button onClick={hangup} className={`${btn} bg-red-600 hover:bg-red-700 text-white px-3 py-1 text-sm`}>📵 Zavěsit</button>
            </div>
          )}
          {deviceError && <span className="text-xs text-red-500 max-w-[180px] truncate" title={deviceError}>{deviceError}</span>}
        </div>
      </div>

      {/* SMS modal */}
      {smsModal && (
        <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/50 p-4" onClick={() => setSmsModal(null)}>
          <div className={`w-full max-w-sm rounded-xl border shadow-2xl ${bg}`} onClick={e => e.stopPropagation()}>
            <div className={`px-4 py-3 border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-200'} flex items-center justify-between`}>
              <span className="font-semibold">💬 Nová SMS</span>
              <button onClick={() => setSmsModal(null)} className="text-lg">✕</button>
            </div>
            <div className="p-4 space-y-3">
              <div className="text-sm">Komu: <span className="font-mono">{smsModal.number}</span>{smsModal.companyName ? ` (${smsModal.companyName})` : ''}</div>
              <textarea rows={4} value={smsModal.body} onChange={e => setSmsModal(m => ({ ...m, body: e.target.value }))} placeholder="Text zprávy…"
                className={`w-full px-3 py-2 rounded border text-sm ${isDarkMode ? 'bg-gray-800 border-gray-700 text-white' : 'bg-white border-gray-300'}`} />
              <div className="flex justify-end gap-2">
                <button onClick={() => setSmsModal(null)} className={`${btn} px-3 py-1.5 text-sm ${isDarkMode ? 'bg-gray-700' : 'bg-gray-200'}`}>Zrušit</button>
                <button onClick={sendSms} disabled={!smsModal.body || smsModal.sending} className={`${btn} bg-blue-600 hover:bg-blue-700 text-white px-4 py-1.5 text-sm`}>{smsModal.sending ? 'Odesílám…' : 'Odeslat'}</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );

  return ReactDOM.createPortal(panel, document.body);
}
